import React, { useState } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle 
} from '../../ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * TestEmailDialog - Diagnostic tool to test email configuration
 * Extracted from AdminWeekendsTab.js (Phase 3H)
 * 
 * Props:
 * - open: boolean - controls dialog visibility
 * - onClose: function - called when dialog should close
 * - authToken: string - auth token for API calls
 */
export default function TestEmailDialog({ open, onClose, authToken }) {
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sending, setSending] = useState(false);

  const handleSendTest = async () => {
    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/weekends/admin/test-email`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: testEmailAddress || undefined })
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success(`Test email sent to ${result.sent_to}! (ID: ${result.resend_id?.slice(0,8) || 'N/A'}...)`);
        handleClose();
      } else {
        toast.error(`Email test failed: ${result.error}`);
      }
    } catch (error) {
      toast.error('Failed to test email configuration');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setTestEmailAddress('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Test Email Configuration</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">
              Send test email to:
            </label>
            <Input
              type="email"
              placeholder="Enter email address (e.g., test@example.com)"
              value={testEmailAddress}
              onChange={(e) => setTestEmailAddress(e.target.value)}
              className="mt-1"
              data-testid="test-email-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Leave empty to send to your admin email
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={handleClose}
              data-testid="test-email-cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSendTest}
              disabled={sending}
              data-testid="test-email-send-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Test Email
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
